import { SortOrder } from 'antd/es/table/interface';
import { Flex } from 'antd';
import Table, { TableProps } from 'antd/es/table';
import classNames from 'classnames';

import { ReactComponent as SwapIcon } from '@Icons/additional/swap.svg';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import { SortConfig } from '@Features/types/SortConfig';

import { useMemo } from 'react';
import {
  mapToCustomPageIndex,
  mapToDefaultPageIndex,
  prepareSortConfig,
} from './utils';
import MPagination from './components/Pagination';

// such config is needed to exclude 'Click to cancel sorting' option
const SORT_DIRECTIONS: SortOrder[] = ['ascend', 'descend', 'ascend'];

interface MTableProps extends TableProps {
  totalItems?: number;
  pageSize?: number;
  onChangePage?: (page: number) => void;
  onSort?: (sortConfig: SortConfig | null) => void;
  currentPage?: number;
  hidePagination?: boolean;
}

const MTable: React.FC<MTableProps> = (props): JSX.Element | null => {
  const {
    columns,
    dataSource,
    totalItems,
    pageSize,
    onChangePage,
    onSort,
    currentPage,
    className,
    hidePagination = false,
    scroll = { x: 1500, y: '100%' },
    ...restProps
  } = props;

  const currentPageValue = mapToCustomPageIndex(
    currentPage || TABLE_PAGE_DEFAULT
  );

  const changedColumns = useMemo(
    () =>
      columns?.map((column) => {
        // eslint-disable-next-line
        column.sortIcon = (filtered) => (filtered ? <SwapIcon /> : null);
        return column;
      }),
    [columns]
  );

  const handleChange = (page: number): void => {
    const pageIndex = mapToDefaultPageIndex(page);
    onChangePage?.(pageIndex);
  };

  return (
    <Flex vertical className="trojan-table-container">
      <Table
        {...restProps}
        className={classNames('trojan-table', className)}
        sortDirections={SORT_DIRECTIONS}
        columns={changedColumns}
        dataSource={dataSource}
        pagination={false}
        scroll={restProps?.rowSelection ? undefined : scroll}
        onChange={(_pagination, _filters, sorter, extra) => {
          if (extra.action === 'sort') {
            const sortConfig = prepareSortConfig(sorter);
            onSort?.(sortConfig);
          }
        }}
      />
      {!hidePagination && columns && dataSource && (
        <Flex className="trojan-table-pagination trojan-box-shadow trojan-table-pagination-box-shadow">
          <MPagination
            showQuickJumper={pageSize !== 1}
            pageSize={pageSize}
            showSizeChanger={false}
            current={currentPageValue}
            total={totalItems}
            onChange={handleChange}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default MTable;
